import { RotateCw } from "lucide-react";
import { useT } from "@/src/i18n";
import {
  FLOOR_OBJECT_CATALOG,
  FLOOR_OBJECT_CATEGORIES,
  type FloorObjectDefinition,
} from "@/src/core/engine/floorObjects";
import { useMapStore } from "@/src/store/useMapStore";

/**
 * Miniature vector preview, matching the map renderer's conventions so the
 * picker reads as glyphs rather than a word list. Mirrors WallTypePicker.
 */
function FloorGlyph({ definition }: { definition: FloorObjectDefinition }) {
  const { style } = definition;
  const stroke = definition.dmOnly ? "#d08bce" : "currentColor";
  const parts = [];

  if (style.steps && !style.wedge && !style.spiral) {
    parts.push(<rect key="box" x="4" y="7" width="24" height="10" stroke={stroke} strokeWidth="1.3" fill="none" />);
    for (let index = 1; index < style.steps; index += 1) {
      const x = 4 + (24 * index) / style.steps;
      parts.push(<path key={`t${index}`} d={`M${x} 7 L${x} 17`} stroke={stroke} strokeWidth="1" />);
    }
  }
  if (style.spiral) {
    parts.push(<path key="sp" d="M16 5 A 7 7 0 1 1 9.5 15" stroke={stroke} strokeWidth="1.2" fill="none" />);
    parts.push(<circle key="hub" cx="16" cy="12" r="1.8" fill={stroke} />);
  }
  if (style.rungs) {
    parts.push(<path key="rails" d="M11 4 L11 20 M21 4 L21 20" stroke={stroke} strokeWidth="1.3" />);
    for (let index = 1; index <= style.rungs; index += 1) {
      const y = 4 + (16 * index) / (style.rungs + 1);
      parts.push(<path key={`r${index}`} d={`M11 ${y} L21 ${y}`} stroke={stroke} strokeWidth="1" />);
    }
  }
  if (style.hatch) {
    parts.push(<rect key="h" x="8" y="5" width="16" height="14" stroke={stroke} strokeWidth="1.3" fill="none"
      strokeDasharray={style.hatch === "ceiling" ? "3 2" : undefined} />);
    parts.push(<path key="hd" d="M8 5 L24 19" stroke={stroke} strokeWidth="1" />);
  }
  if (style.pit) {
    parts.push(<ellipse key="p" cx="16" cy="12" rx="9" ry="7" stroke={stroke} strokeWidth="1.3" fill="none" />);
    if (style.pit === "covered") {
      parts.push(<path key="pc" d="M7 12 L25 12" stroke={stroke} strokeWidth="1.2" strokeDasharray="3 2" />);
    } else if (style.pit === "spiked") {
      parts.push(<path key="ps" d="M9 12 L13 8 L16 12 L19 8 L23 12" stroke={stroke} strokeWidth="1.1" fill="none" />);
    } else {
      parts.push(<path key="po" d="M11 8 L15 16 M16 8 L20 16" stroke={stroke} strokeWidth="1" />);
    }
  }
  if (style.rings) {
    for (let index = 0; index < style.rings; index += 1) {
      const scale = 1 - index * (0.7 / style.rings);
      parts.push(<ellipse key={`ring${index}`} cx="16" cy="12" rx={9 * scale} ry={7 * scale}
        stroke={stroke} strokeWidth="1.1" fill="none" />);
    }
  }
  if (style.wedge) {
    parts.push(<path key="w" d="M5 6 L27 10 L27 14 L5 18 Z" stroke={stroke} strokeWidth="1.2" fill="none" />);
  }
  if (style.direction) {
    const up = style.direction === "up";
    const tip = up ? 27 : 5, tail = up ? 5 : 27, head = up ? -4 : 4;
    parts.push(<path key="arrow" d={`M${tail} 12 L${tip} 12 M${tip + head} 9 L${tip} 12 L${tip + head} 15`}
      stroke={stroke} strokeWidth="1.4" fill="none" strokeLinecap="round" />);
  }

  return <svg viewBox="0 0 32 24" className="h-6 w-8" aria-hidden="true" fill="none">{parts}</svg>;
}

export function FloorTypePicker() {
  const selected = useMapStore((state) => state.selectedFloorType);
  const setSelected = useMapStore((state) => state.setSelectedFloorType);
  const rotation = useMapStore((state) => state.floorPlacementRotation);
  const rotate = useMapStore((state) => state.rotateFloorPlacement);
  const t = useT();

  return (
    <section className="border-t border-white/8 px-3 py-3">
      <p className="mb-1 px-1 text-[9px] font-bold uppercase tracking-[0.18em] text-stone-600">
        {t("floor.pickerTitle")}
      </p>
      <p className="mb-2 px-1 text-[9px] leading-4 text-stone-600">{t("floor.pickerHint")}</p>

      <button
        type="button"
        onClick={() => rotate()}
        data-testid="floor-rotate"
        className="mb-2 flex h-8 w-full items-center justify-center gap-2 rounded-lg border border-white/8 bg-white/[0.03] text-[10px] font-semibold text-stone-300 transition hover:border-white/20 hover:text-white"
      >
        <RotateCw size={12} /> {t("floor.rotation", { degrees: rotation })}
      </button>

      {FLOOR_OBJECT_CATEGORIES.map((category) => {
        const entries = FLOOR_OBJECT_CATALOG.filter((entry) => entry.category === category);
        if (entries.length === 0) return null;
        return (
          <div key={category} className="mb-2 last:mb-0">
            <p className="mb-1 px-1 text-[8px] font-bold uppercase tracking-[0.16em] text-stone-700">
              {t(`floorCategory.${category}`)}
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {entries.map((entry) => {
                const active = selected === entry.id;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setSelected(entry.id)}
                    aria-pressed={active}
                    title={`${t(entry.labelKey)}${entry.dmOnly ? ` · ${t("wall.dmOnly")}` : ""}`}
                    data-floortype={entry.id}
                    className={`relative flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl border p-1 text-center transition ${active ? "border-[#c3463d] bg-[#a8322c]/25 text-white" : "border-white/6 bg-white/[0.025] text-stone-400 hover:border-white/15 hover:bg-white/[0.06] hover:text-stone-100"}`}
                  >
                    <FloorGlyph definition={entry} />
                    <span className="text-[8px] font-medium leading-none">{t(entry.labelKey)}</span>
                    {entry.dmOnly && (
                      <span className="absolute end-1 top-1 h-1.5 w-1.5 rounded-full bg-[#d08bce]"
                        title={t("wall.dmOnlyHint")} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </section>
  );
}
